import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import './Summary.scss'
import { IWaterwaySummaryStation, SummaryVM } from './SummaryVM';
import { ActionsMenu } from 'cd-eft/components/Design/common/ActionsMenu';
import { formatDateShort } from 'cd-eft/utils/formatDate';
import { DesignSummaryButtons } from 'cd-eft/components/Design/common/DesignSummaryButtons';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { DropdownPopover } from 'cd-eft/components/common/DropdownPopover/DropdownPopover'; 
import { Table, Modal, ModalHeader, ModalBody, ModalFooter, Container } from 'reactstrap';
import { PopupContainer } from '../../../../common/SummaryDetails/SDetails';
import { mdiHelpCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { toTitleCase } from 'cd-eft/utils/stringUtilities';

interface ISummaryProps {
  onClickEdit: any;
  onClickAddButtonOnSummaryPage: any;
}

const Summary = (props: ISummaryProps) => {
  const vm = useRef(new SummaryVM( props.onClickEdit)).current;

  
  useEffect(()=>{
    vm.loadData();
  },[])

  const tooltipStandardMessage =
  'Data invalid for the listed items below. Input changes detected. Please Edit and recalculate.';

  const Cols =
    [
      { name: "select", title: "Select"},
      { name: "name", title: "Name"},
      { name: "surface", title: "Waterway Type"},
      { name: "alignment", title: "Channel Type"},
      { name: "length", title: "Length(ft)"},
      { name: "hydrology", title: "Max Top Width"},
      { name: "noOfreaches", title: "# of reaches"},
      {
        name: 'warnings',
        title: 'Warnings',
        enableTooltip: true
      },
      { name: "lastUpdatedDate", title: "Last Updated"},
      { name: "actions", title: "Actions"},
    ];

  return (
    <>
     
      <h2 className='summary-title'><strong>Summary</strong></h2>
      <div className="design-summary-table">
        <Table className="set-table">
          <thead className="table-header">
          <tr>
              {Cols.map((e, index) => (
                <th key={'key-waterwaySummary-title' + index} className={e.name === 'warnings' ? 'tooltip-header' : ''}>
                  <span>
                    {e.title}&nbsp;
                    {e.enableTooltip && vm.showIcon
                      ? <StaticTooltip tooltipMessage={tooltipStandardMessage} tooltipSide="bottom" id="waterway-warnings-tooltip" title="Warnings info" />
                      : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          {vm.waterwaySummaryStationList.length>0 ? (
            <tbody>
              {vm.waterwaySummaryStationList.map((e:IWaterwaySummaryStation,index:number)=>(
                <tr key={'key-waterwaySummary-' + index}>
                <td>
                  <input
                    id={'select' + index}
                    name="select"
                    type="checkbox"
                    checked={vm.waterwaySummaryStationList[index].select}
                    onChange={()=>{vm.handleCheckboxChange(index)}}
                    disabled={vm.notificationDictionary[vm.waterwaySummaryStationList[index].alignedDesignElementId] !== null}
                    aria-label={`select-${e.name}`}
                  />
                </td>
                <td>
                    {e.name}
                </td>
                <td>
                    {e.waterwayType}
                </td>
                <td>
                   {toTitleCase(e.channelType)}
                </td>
                <td>
                    {e.length.toFixed(1)}
                </td>
                <td>
                    {e.maxTopWidth.toFixed(1)}
                </td>
                <td>
                    {e.reachCount}
                </td>
                <td className="list-text">
                  {vm.notificationDictionary[e.alignedDesignElementId]? 
                    <DropdownPopover buttonText="View List">
                    <ul className="add-list-reset">
                      {vm.notificationDictionary[e.alignedDesignElementId]?.surface!==0 ? <li className='list-content'>
                        <span className='text'>Ground Surface</span> <button className= "question-button" title= {`Ground Surface`} onClick={()=>vm.onClickInformationToggle('Ground Surface')}>
                          <Icon className='help-icon' path={ mdiHelpCircleOutline} size={1} /> 
                        </button>
                      </li> : null}
                      {vm.notificationDictionary[e.alignedDesignElementId]?.alignment !== 0? <li className='list-content'>
                        <span className='text'>Alignment</span> <button className= "question-button" title= {`Alignment`} onClick={()=>vm.onClickInformationToggle('Alignment')}>
                          <Icon className='help-icon' path={ mdiHelpCircleOutline} size={1} />
                        </button>
                      </li>: null}
                      {vm.notificationDictionary[e.alignedDesignElementId]?.designControl !== 0? <li className='list-content'>
                        <span className='text'>Design Controls</span> <button className= "question-button" title= {`Design Controls`} onClick={()=>vm.onClickInformationToggle('Design Controls')}>
                          <Icon className='help-icon' path={ mdiHelpCircleOutline} size={1} />
                        </button>
                      </li>: null}
                      {vm.notificationDictionary[e.alignedDesignElementId]?.peakFlow !== 0? <li className='list-content'>
                        <span className='text'>Peak Flow Q</span> <button className= "question-button" title= {`Peak Flow Q`} onClick={()=>vm.onClickInformationToggle('Peak Flow Q')}>
                          <Icon className='help-icon' path={ mdiHelpCircleOutline} size={1} /> 
                        </button>
                      </li>: null}
                      {vm.notificationDictionary[e.alignedDesignElementId]?.reaches !== 0? <li className='list-content'>
                        <span className='text'>Reaches</span> <button className= "question-button" title= {`Reaches` } onClick={()=>vm.onClickInformationToggle('Reaches')}>
                          <Icon className='help-icon' path={ mdiHelpCircleOutline} size={1} /> 
                        </button>
                      </li> :null}
                    </ul>
                  </DropdownPopover>
                  :"None"}
                </td>
                <td>
                    {formatDateShort(Number(e.lastUpdatedDate))}
                </td>
                <td>
                  <ActionsMenu i={index} item={e} elementActions={vm.elementActions} />
                </td>
              </tr>
              ))}
            </tbody>
          ):null}
        </Table>
        {vm.waterwaySummaryStationList.length === 0 ? (
          <>
            <h4 className="note-summary">Add your first Waterway Design</h4>
          </>
        ) : null}
      </div>
      <DesignSummaryButtons
        panel="Waterway"
        disableAdd={false}
        disableRefreshElements={vm.waterwaySummaryStationList.length === 0}
        disableRebuildSurface={vm.waterwaySummaryStationList.length === 0}
        buttontype={false}
        add={props.onClickAddButtonOnSummaryPage}
        refreshElements={vm.refreshElements}
        rebuildSurface={vm.callRebuildSurface}
      />

      {vm.onClickInformation ? (
      <div>
        <Modal
        isOpen={vm.onClickInformation} 
        toggle={vm.onClickInformationToggle}
        centered={true} 
        className="eft-action-modal"
        aria-label={vm.headerTitle}
        >
          <ModalHeader toggle= {vm.onClickInformationToggle} className='header-info-detail'>
            <div className="display-flex flex-align-center">
              <strong>{`Information Details on ${vm.headerTitle}`}</strong> 
            </div>
            <a className="href-info-detail" href="/design/waterwaysummarydetails" target="_blank">
              <strong>Open Information in a New Window</strong>
            </a>
          </ModalHeader>
          <ModalBody>
            <Container>
              {PopupContainer({title: vm.headerTitle, design: 4})}
            </Container>
          </ModalBody>
          <ModalFooter>
            <button className="usa-button usa-button--outline" onClick={vm.onClickInformationToggle}>
              Close
            </button>
          </ModalFooter>
        </Modal>
      </div>
      ) : null }
    </>
  );
};

export const SummaryComp = observer(Summary);
